import React, {ReactNode, useEffect, useRef, useState} from "react";
import {getUser} from "../api/getUser";
import {logout} from "../api/logout";
import posthog from "posthog-js";
import {getShop} from "../api/getShop";
import {getWabaId} from "../api/getWabaId";
import {hasEffectivePricingPlan} from "../utils/pricingPlanStatus";

interface UserProps {
    email: string;
    name: string;
    shopDomains: string[];
    shops?: { name: string, friendlyName: string }[];
    role: string;
}

interface UserContextProviderProps {
    children: ReactNode | ReactNode[];
}

export interface UserContextProps {
    user: UserProps | null;
    isInitialLoadDone: boolean;
    selectedDomain?: string;
    isAdmin: boolean;
    onboardingStatus: {
        isOnboardingStatusLoaded: boolean;
        isFullyOnboarded: boolean;
        hasContacts: boolean;
        hasContactEmail: boolean;
        hasContactPhone: boolean;
        hasPricingPlan: boolean;
        hasPhone: boolean;
        hasWaba: boolean;
    };
    actions: {
        getData: () => any | null;
        getUser: () => any | null;
        getFirstName: () => string;
        setSelectedDomain: (newDomain: string) => void;
        logout: () => void;
        refreshOnboardingStatus: () => Promise<void>;
    };
}

export interface PlatformParamsProps {
    ott?: string | null;
    platform?: string | null;
}

export const defaultUserContextValue = {
    user: null,
    isInitialLoadDone: false,
    isAdmin: false,
    onboardingStatus: {
        isOnboardingStatusLoaded: false,
        isFullyOnboarded: false,
        hasContacts: false,
        hasContactEmail: false,
        hasContactPhone: false,
        hasPricingPlan: false,
        hasPhone: false,
        hasWaba: false,
    },
    actions: {
        getData: () => null,
        getUser: () => null,
        getFirstName: () => '',
        setSelectedDomain: (newDomain: string) => null,
        logout: () => null,
        refreshOnboardingStatus: async () => undefined,
    },
}

export const UserContext = React.createContext<UserContextProps>(defaultUserContextValue);

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
    const [user, setUser] = useState<UserProps | null>(null);
    const [selectedDomain, setSelectedDomain] = useState<string | undefined>();
    const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const onboardingRequestSeq = useRef(0);
    const [onboardingStatus, setOnboardingStatus] = useState<UserContextProps["onboardingStatus"]>({
        isOnboardingStatusLoaded: false,
        isFullyOnboarded: false,
        hasContacts: false,
        hasContactEmail: false,
        hasContactPhone: false,
        hasPricingPlan: false,
        hasPhone: false,
        hasWaba: false,
    });
    const localStorageSelectedDomain = localStorage.getItem('selectedDomain');

    const getData = async () => {
        const response = await getUser();
        if(response.data.status === 'found') {
            // console.log("user: ", response.data.user);
            setUser(response.data.user);
            window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "AUTH_STATE", authenticated: true, username: response.data.user.name, domain: response.data.user?.shopDomains?.[0], token: localStorage.getItem('token') }));
            setIsAdmin(response.data.user.role === 'admin');
        }
        setIsInitialLoadDone(true);
    }

    const getFirstName = () => {
        return user?.name?.split(' ')[0] || '';
    }

    const logoutUser = async () => {
        setUser(null);
        setOnboardingStatus({
            isOnboardingStatusLoaded: false,
            isFullyOnboarded: false,
            hasContacts: false,
            hasContactEmail: false,
            hasContactPhone: false,
            hasPricingPlan: false,
            hasPhone: false,
            hasWaba: false,
        });
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "AUTH_STATE", authenticated: false, username: '' }));
        await logout();
        localStorage.removeItem('token');
    }

    const refreshOnboardingStatus = async () => {
        const requestId = ++onboardingRequestSeq.current;

        if (!selectedDomain) {
            setOnboardingStatus({
                isOnboardingStatusLoaded: false,
                isFullyOnboarded: false,
                hasContacts: false,
                hasContactEmail: false,
                hasContactPhone: false,
                hasPricingPlan: false,
                hasPhone: false,
                hasWaba: false,
            });
            return;
        }

        // Mark as loading while keeping previous resolved values to avoid
        // flashing fallback UI with temporary false flags.
        setOnboardingStatus((prev) => ({ ...prev, isOnboardingStatusLoaded: false }));

        try {
            const [shopResponse, wabaResponse] = await Promise.all([
                getShop(selectedDomain),
                getWabaId(selectedDomain),
            ]);

            // Ignore stale async results if a newer refresh already started.
            if (requestId !== onboardingRequestSeq.current) return;

            const shopData: any = shopResponse?.data || {};
            const wabaData: any = wabaResponse?.data?.waba || wabaResponse?.data || {};
            const primaryPhone = wabaData?.phones?.[0] || null;

            const hasContactEmail = Boolean(shopData?.contactEmail || shopData?.contact_email);
            const hasContactPhone = Boolean(shopData?.contactPhone || shopData?.contact_phone);
            const hasContacts = hasContactEmail && hasContactPhone;

            const shopId = Number(shopData?.id || 0);
            const pricingPlanId = Number(shopData?.pricingPlanId || shopData?.pricing_plan_id || shopData?.pricingPlan?.id || 0);
            const hasPricingPlan = hasEffectivePricingPlan(pricingPlanId, shopId);

            const hasPhone = Boolean(
                (primaryPhone?.phoneNumber || primaryPhone?.phone_number || wabaData?.phoneNumber) &&
                (primaryPhone?.active !== false)
            );
            const hasWaba = Boolean(
                (wabaData?.whatsappBusinessAccountId || wabaData?.whatsapp_business_account_id || wabaData?.providerSenderId || wabaData?.provider_sender_id) &&
                wabaData?.active !== false &&
                !wabaData?.deleted
            );

            setOnboardingStatus({
                isOnboardingStatusLoaded: true,
                isFullyOnboarded: hasPricingPlan && hasWaba && hasPhone,
                hasContacts,
                hasContactEmail,
                hasContactPhone,
                hasPricingPlan,
                hasPhone,
                hasWaba,
            });
        } catch (error) {
            if (requestId !== onboardingRequestSeq.current) return;
            // Keep previously known values, only end loading state.
            setOnboardingStatus((prev) => ({ ...prev, isOnboardingStatusLoaded: true }));
        }
    };

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        if(user?.shopDomains?.length) {
            if(localStorageSelectedDomain && user?.shopDomains.includes(localStorageSelectedDomain)) {
                setSelectedDomain(localStorageSelectedDomain);
            } else {
                setSelectedDomain(user?.shopDomains[0]);
                localStorage.setItem('selectedDomain', user?.shopDomains[0]);
            }
        }
    }, [user?.shopDomains, localStorageSelectedDomain]);

    useEffect(() => {
        const shop = user?.shops?.find(shop => shop.name === selectedDomain);
        if(!shop) return;
        posthog.identify(shop.friendlyName, {
            name: user?.name,
            email: user?.email,
            domain: selectedDomain
        });
    }, [selectedDomain, user])

    useEffect(() => {
        refreshOnboardingStatus();
    }, [selectedDomain]);

    return (
        <UserContext.Provider value={{
            user,
            isInitialLoadDone,
            selectedDomain,
            isAdmin,
            onboardingStatus,
            actions: {
                getData,
                getUser,
                getFirstName,
                setSelectedDomain,
                logout: logoutUser,
                refreshOnboardingStatus,
            }
        }}>
            {children}
        </UserContext.Provider>
    );
}
